import React from 'react';
import ReactDOM from 'react-dom';
import Widget from './Widget';
import { getPlain } from 'src/utils/request-client';
import { isLocal, getBrandSettings, logError, isEmpty, getStaticUrl } from 'src/utils/helpers';
import BrokerService from 'src/utils/broker-service';
import * as Sentry from '@sentry/browser';
import { getBrandingCssUri } from 'src/utils/theme'
import 'src/commonIndex.js';

const staticUrl = getStaticUrl();

const sentryDnsAndToken = process.env.REACT_APP_SENTRY || null;

Sentry.init({
  dsn: isLocal() ? null : sentryDnsAndToken,
  release: process.env.REACT_APP_VERSION,
  maxBreadcrumbs: 50,
  // Regex list of URLs to include in Sentry reports (just our app since this is for widget)
  whitelistUrls: [
    new RegExp(`${staticUrl}.*`),
    new RegExp(`${process.env.REACT_APP_API_BASE}.*`),
  ],
});

/**
 * Construct a class which will be used from widget script.
 */
export default class PathwayWidget {
  static el;

  static mount(properties) {
    // Set broker/client/kiosk instance from widget property,
    // since we don't read it from URL (like with normal/direct mode).
    BrokerService.setBroker(properties.broker);

    /**
     * Inject CSS needed for widget in "full-size" mode.
     * When it's fully expanded above parent website,
     * so we have to "freeze" parent website to avoid scrolling issues.
     */
    function setParentSiteStyle() {
      // Create <style> element
      const head = document.head || document.getElementsByTagName('head')[0];
      const style = document.createElement('style');
      style.setAttribute('data-meta', 'kiosk-widget-full-screen-freeze-parent-site');
      style.setAttribute('type', 'text/css');

      // Inject <style> to <head>
      head.appendChild(style);

      // Add actual CSS into our <style> element
      const css = `
        html.kiosk-widget-mobile-full-size,
        html.kiosk-widget-mobile-full-size>body {
            position: static !important;
            overflow: hidden !important;
        }
        html.kiosk-widget-mobile-full-size>body {
            height: 0 !important;
            margin: 0 !important;
        }`;
      if (style.styleSheet){
        // This is required for IE8 and below.
        style.styleSheet.cssText = css;
      } else {
        style.appendChild(document.createTextNode(css));
      }
    }
    setParentSiteStyle();

    /**
     * Make a request to our backend API to get settings for current broker/user/kiosk instance,
     * then extract and return colors.
     *
     * @return {Promise<{}|null|*>}
     */
    async function fetchColorsFromSettings() {
      try {
        const settings = await getBrandSettings('w');

        if (typeof settings !== 'object' || isEmpty(settings) || settings.hasOwnProperty('error')) {
          return {};
        }

        return settings && settings.data && settings.data.data && settings.data.data.colors;
      } catch (e) {
        logError('Get colors from brand settings', e);
        return null;
      }
    }

    /**
     * Fetch CSS with color definitions from our app server (Node.js/Express).
     * Because it has to:
     * 1. read actual file from `server/colors.less`,
     * 2. replace color variables in that file with these colors we're sending here,
     * 3. return CSS with replaces colors.
     *
     * @param colors
     * @return {Promise<any>}
     */
    async function fetchBrandingCSS(colors) {
      const response = await getPlain(getBrandingCssUri(colors));

      return response && response.data && response.data.data && response.data.data.css;
    }

    /**
     * This is CSS for whole kiosk app (inside the widget/iframe).
     *
     * It doesn't go through normal webpack import, because it needs to be attached
     * to iframe which is not yet generated (that happens "later" in widget/src/Widget).
     *
     * So here we read generated main.css file from build folder and we'll later inject it to iframe head.
     *
     * @return {Promise<any>}
     */
    async function fetchCoreCss() {
      const response = await getPlain(`${staticUrl}/get-generated-styles.json`);
      return response && response.data && response.data.data;
    }

    async function doRender() {
      if (PathwayWidget.el) {
        throw new Error('PathwayWidget is already mounted, unmount first');
      }

      const colors = await fetchColorsFromSettings();
      let brandingStyles = '';
      if(!isEmpty(colors)) {
        brandingStyles = await fetchBrandingCSS(colors);
      }

      const coreStyles = await fetchCoreCss();

      const el = document.createElement('div');
      el.id = "pathway-widget";
      document.body.appendChild(el);
      ReactDOM.render(<Widget
        colors={colors}
        css={{
          brandingStyles,
          coreStyles,
        }}
        openOnLoad={properties.hasOwnProperty('openOnLoad') ? properties.openOnLoad : true }
        sound={properties.hasOwnProperty('sound') ? properties.sound : true }
        widgetLanguage={properties.hasOwnProperty('language') ? properties.language : null }
        onIsActiveChange={properties.hasOwnProperty('onIsActiveChange') ? properties.onIsActiveChange : null}
        { ...properties }
      />, el);
      PathwayWidget.el = el;
    }

    if (document.readyState === 'complete') {
      doRender();
    } else {
      window.addEventListener('load', () => {
        setTimeout(() => doRender());
      });
    }
  }

  static unmount() {
    if (!PathwayWidget.el) {
      throw new Error('PathwayWidget is not mounted, mount first');
    }
    ReactDOM.unmountComponentAtNode(PathwayWidget.el);
    PathwayWidget.el.parentNode.removeChild(PathwayWidget.el);
    PathwayWidget.el = null;
  }
}
